import React, { useEffect, useState } from "react";
import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  setDoc,
  serverTimestamp,
  Timestamp,
  query,
  where
} from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions, httpsCallable } from "firebase/functions";
import { useAuth, UserRole } from "../context/AuthContext";
import firebaseConfig from "../fb.json";
import "../styles/settings.css";

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
const db = getFirestore(app);
const functions = getFunctions(app);
if (window.location.hostname === "localhost" || window.location.protocol === "file:") {
  connectFunctionsEmulator(functions, "localhost", 5005);
}

const ROLES: UserRole[] = ["editor", "dev", "admin", "owner"];

interface TeamMember {
  uid: string;
  email: string;
  displayName: string;
  photoURL: string | null;
  role: UserRole;
  projects: string[];
  lastLoginAt: Timestamp | null;
}

interface Invite {
  id: string;
  email: string;
  role: UserRole;
  projects: string[];
  invitedBy: string;
  createdAt: Timestamp;
  expiresAt: Timestamp;
  used: boolean;
}

const Settings = () => {
  const auth = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);

  // Invite form
  const [invEmail, setInvEmail] = useState("");
  const [invRole, setInvRole] = useState<UserRole>("editor");
  const [invSending, setInvSending] = useState(false);
  const [invMessage, setInvMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load team members
      const usersSnap = await getDocs(collection(db, "users"));
      const membersList: TeamMember[] = [];
      usersSnap.forEach((d) => {
        const data = d.data();
        membersList.push({
          uid: d.id,
          email: data.email || "",
          displayName: data.displayName || "",
          photoURL: data.photoURL || null,
          role: data.role || "editor",
          projects: data.projects || [],
          lastLoginAt: data.lastLoginAt || null
        });
      });
      membersList.sort((a, b) => {
        const roleOrder = { owner: 0, admin: 1, dev: 2, editor: 3 };
        return (roleOrder[a.role] || 9) - (roleOrder[b.role] || 9);
      });
      setMembers(membersList);

      // Load pending invites
      const invSnap = await getDocs(
        query(collection(db, "invites"), where("used", "==", false))
      );
      const invitesList: Invite[] = [];
      invSnap.forEach((d) => {
        const data = d.data();
        invitesList.push({
          id: d.id,
          email: data.email || "",
          role: data.role || "editor",
          projects: data.projects || [],
          invitedBy: data.invitedBy || "",
          createdAt: data.createdAt,
          expiresAt: data.expiresAt,
          used: data.used || false
        });
      });
      setInvites(invitesList);
    } catch (err) {
      console.error("[Settings] Error loading data:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const changeRole = async (uid: string, newRole: UserRole) => {
    if (uid === auth.user?.uid) return;
    try {
      await updateDoc(doc(db, "users", uid), { role: newRole });
      setMembers((prev) =>
        prev.map((m) => (m.uid === uid ? { ...m, role: newRole } : m))
      );
    } catch (err) {
      console.error("[Settings] Error changing role:", err);
    }
  };

  const removeMember = async (uid: string, email: string) => {
    if (uid === auth.user?.uid) return;
    if (!window.confirm(`Remove ${email} from the team?`)) return;
    try {
      await deleteDoc(doc(db, "users", uid));
      setMembers((prev) => prev.filter((m) => m.uid !== uid));
    } catch (err) {
      console.error("[Settings] Error removing member:", err);
    }
  };

  const cancelInvite = async (inviteId: string) => {
    try {
      await deleteDoc(doc(db, "invites", inviteId));
      setInvites((prev) => prev.filter((i) => i.id !== inviteId));
    } catch (err) {
      console.error("[Settings] Error cancelling invite:", err);
    }
  };

  const sendInvite = async () => {
    const email = invEmail.trim().toLowerCase();
    if (!email) return;

    setInvSending(true);
    setInvMessage(null);

    try {
      // Check if user already exists
      const existingMember = members.find((m) => m.email.toLowerCase() === email);
      if (existingMember) {
        setInvMessage({ type: "err", text: `${email} is already a team member.` });
        setInvSending(false);
        return;
      }

      // Create invite
      const token = crypto.randomUUID();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      const inviteData = {
        email,
        role: invRole,
        projects: ["*"],
        invitedBy: auth.user?.email || "",
        createdAt: serverTimestamp(),
        expiresAt: Timestamp.fromDate(expiresAt),
        used: false
      };

      await setDoc(doc(db, "invites", token), inviteData);

      // Try to send email via Cloud Function
      try {
        const sendInviteEmail = httpsCallable(functions, "sendInviteEmail");
        await sendInviteEmail({ email, token, role: invRole, origin: window.location.origin });
      } catch {
        // Email sending is optional — invite link still works
        console.warn("[Settings] Could not send invite email, but invite was created.");
      }

      const inviteUrl = `${window.location.origin}/invite/${token}`;
      setInvMessage({ type: "ok", text: `Invite created! Link: ${inviteUrl}` });
      setInvEmail("");
      loadData();
    } catch (err: any) {
      setInvMessage({ type: "err", text: err.message || "Failed to create invite." });
    }

    setInvSending(false);
  };

  const formatDate = (ts: Timestamp | null) => {
    if (!ts) return "—";
    return ts.toDate().toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="settings">
        <h1 className="settings__title">Settings</h1>
        <p className="settings__loading">Loading...</p>
      </div>
    );
  }

  return (
    <div className="settings">
      <h1 className="settings__title">Settings</h1>

      {/* Team Members */}
      <section className="settings__section">
        <h2 className="settings__section-title">Team Members</h2>
        <div className="settings__table-wrap">
          <table className="settings__table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Role</th>
                <th>Last Login</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const isSelf = m.uid === auth.user?.uid;
                const isOwnerTarget = m.role === "owner";
                const canModify = !isSelf && (!isOwnerTarget || auth.isOwner);
                return (
                  <tr key={m.uid}>
                    <td>
                      <div className="settings__member">
                        {m.photoURL && (
                          <img className="settings__member-avatar" src={m.photoURL} alt="" width="28" height="28" referrerPolicy="no-referrer" />
                        )}
                        <div>
                          <div className="settings__member-name">
                            {m.displayName || m.email}
                            {isSelf && <span className="settings__badge">you</span>}
                          </div>
                          <div className="settings__member-email">{m.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {canModify ? (
                        <select
                          className="settings__select"
                          value={m.role}
                          onChange={(e) => changeRole(m.uid, e.target.value as UserRole)}
                        >
                          {ROLES.filter((r) => auth.isOwner || r !== "owner").map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={`settings__role-badge settings__role-badge--${m.role}`}>{m.role}</span>
                      )}
                    </td>
                    <td className="settings__date">{formatDate(m.lastLoginAt)}</td>
                    <td>
                      {canModify && (
                        <button
                          className="settings__remove-btn"
                          onClick={() => removeMember(m.uid, m.email)}
                          title="Remove member"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Pending Invites */}
      {invites.length > 0 && (
        <section className="settings__section">
          <h2 className="settings__section-title">Pending Invites</h2>
          <div className="settings__table-wrap">
            <table className="settings__table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Invited By</th>
                  <th>Expires</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => (
                  <tr key={inv.id}>
                    <td>{inv.email}</td>
                    <td><span className={`settings__role-badge settings__role-badge--${inv.role}`}>{inv.role}</span></td>
                    <td className="settings__date">{inv.invitedBy}</td>
                    <td className="settings__date">{formatDate(inv.expiresAt)}</td>
                    <td>
                      <button
                        className="settings__remove-btn"
                        onClick={() => cancelInvite(inv.id)}
                        title="Cancel invite"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Invite New Member */}
      <section className="settings__section">
        <h2 className="settings__section-title">Invite New Member</h2>
        <div className="settings__invite-form">
          <div className="settings__invite-row">
            <div className="settings__field settings__field--email">
              <label className="settings__label" htmlFor="inv-email">Email</label>
              <input
                id="inv-email"
                className="settings__input"
                type="email"
                placeholder="user@company.com"
                value={invEmail}
                onChange={(e) => setInvEmail(e.target.value)}
              />
            </div>
            <div className="settings__field settings__field--role">
              <label className="settings__label" htmlFor="inv-role">Role</label>
              <select
                id="inv-role"
                className="settings__select"
                value={invRole}
                onChange={(e) => setInvRole(e.target.value as UserRole)}
              >
                {ROLES.filter((r) => auth.isOwner || r !== "owner").map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <button
              className="settings__invite-btn"
              onClick={sendInvite}
              disabled={invSending || !invEmail.trim()}
            >
              {invSending ? "Sending..." : "Send Invite"}
            </button>
          </div>
          {invMessage && (
            <div className={`settings__msg settings__msg--${invMessage.type}`}>
              {invMessage.text}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Settings;
